
import React from "react";
import Container from "./ui/Container";
import { Clock, DollarSign, PieChart, Check } from "lucide-react";

const Problem = () => {
  const benefits = [
    {
      icon: <Clock className="w-5 h-5 text-tech-cyan" />,
      text: "Save 20+ hours per week on repetitive tasks",
    },
    {
      icon: <DollarSign className="w-5 h-5 text-tech-cyan" />,
      text: "Reduce operational costs by up to 30%",
    },
    {
      icon: <PieChart className="w-5 h-5 text-tech-cyan" />,
      text: "Improve customer response time by 80%",
    },
  ];

  return (
    <section className="py-20 relative overflow-hidden" id="about">
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-tech-offwhite to-transparent"></div>
      
      <Container>
        <div className="grid md:grid-cols-2 gap-12 md:gap-8 items-center">
          <div>
            <div className="relative">
              <div className="glass-panel rounded-2xl p-6 shadow-xl overflow-hidden">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 flex items-center bg-red-50 rounded-lg p-4 opacity-0 animate-fade-in" style={{animationDelay: "0.2s", animationFillMode: "forwards"}}>
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mr-4">
                      <div className="w-6 h-6 bg-red-500 rounded-full animate-pulse-soft"></div>
                    </div>
                    <div>
                      <div className="h-3 bg-red-200 rounded-full w-32 mb-2"></div>
                      <div className="h-2 bg-red-100 rounded-full w-24"></div>
                    </div>
                  </div>
                  
                  <div className="bg-tech-blue/5 rounded-lg p-4 opacity-0 animate-fade-in" style={{animationDelay: "0.3s", animationFillMode: "forwards"}}>
                    <div className="w-8 h-8 rounded-full bg-tech-blue/10 mb-3 flex items-center justify-center">
                      <div className="w-4 h-4 bg-tech-blue rounded-sm"></div>
                    </div>
                    <div className="h-2 bg-tech-blue/20 rounded-full w-full mb-2"></div>
                    <div className="h-2 bg-tech-blue/20 rounded-full w-3/4"></div>
                  </div>
                  
                  <div className="bg-amber-50 rounded-lg p-4 opacity-0 animate-fade-in" style={{animationDelay: "0.4s", animationFillMode: "forwards"}}>
                    <div className="w-8 h-8 rounded-full bg-amber-100 mb-3 flex items-center justify-center">
                      <div className="w-4 h-4 bg-amber-400 rounded-sm"></div>
                    </div>
                    <div className="h-2 bg-amber-200 rounded-full w-full mb-2"></div>
                    <div className="h-2 bg-amber-200 rounded-full w-3/4"></div>
                  </div>
                  
                  <div className="bg-tech-cyan/5 rounded-lg p-4 opacity-0 animate-fade-in" style={{animationDelay: "0.5s", animationFillMode: "forwards"}}>
                    <div className="w-8 h-8 rounded-full bg-tech-cyan/10 mb-3 flex items-center justify-center">
                      <div className="w-4 h-4 bg-tech-cyan rounded-sm"></div>
                    </div>
                    <div className="h-2 bg-tech-cyan/20 rounded-full w-full mb-2"></div>
                    <div className="h-2 bg-tech-cyan/20 rounded-full w-2/3"></div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-4 opacity-0 animate-fade-in" style={{animationDelay: "0.6s", animationFillMode: "forwards"}}>
                    <div className="w-8 h-8 rounded-full bg-green-100 mb-3 flex items-center justify-center">
                      <div className="w-4 h-4 bg-green-500 rounded-sm"></div>
                    </div>
                    <div className="h-2 bg-green-200 rounded-full w-full mb-2"></div>
                    <div className="h-2 bg-green-200 rounded-full w-4/5"></div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -z-10 -bottom-4 -right-4 w-32 h-32 bg-tech-orange opacity-5 rounded-full blur-3xl animate-pulse-soft"></div>
              <div className="absolute -z-10 -top-4 -left-4 w-32 h-32 bg-tech-blue opacity-10 rounded-full blur-3xl animate-float"></div>
            </div>
          </div>
          
          <div>
            <div className="space-y-6 max-w-xl">
              <div className="inline-block px-3 py-1 rounded-full bg-tech-orange/10 text-tech-orange font-medium text-sm mb-2 opacity-0 animate-fade-in" style={{animationDelay: "0.2s", animationFillMode: "forwards"}}>
                The Problem
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-tech-blue leading-tight opacity-0 animate-fade-in" style={{animationDelay: "0.3s", animationFillMode: "forwards"}}>
                Are You Wasting Time on Tasks That Could Be Automated?
              </h2>
              
              <p className="text-lg text-gray-700 opacity-0 animate-fade-in" style={{animationDelay: "0.4s", animationFillMode: "forwards"}}>
                Every minute spent on repetitive tasks is lost revenue. Our custom software solutions eliminate inefficiencies, cut costs, and free you to focus on growing your business.
              </p>
              
              <div className="pt-4 space-y-4 opacity-0 animate-fade-in" style={{animationDelay: "0.5s", animationFillMode: "forwards"}}>
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 p-1 bg-white rounded-full shadow-sm mr-3">
                      <div className="bg-tech-blue/5 rounded-full p-1">
                        {benefit.icon}
                      </div>
                    </div>
                    <p className="text-gray-700">{benefit.text}</p>
                  </div>
                ))}
              </div>
              
              <div className="pt-4 border-t border-gray-200 opacity-0 animate-fade-in" style={{animationDelay: "0.6s", animationFillMode: "forwards"}}>
                <div className="flex items-center">
                  <div className="bg-green-50 rounded-full p-1 mr-2">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Our clients report</span>: Implementing our automation solutions pays for itself within 3-6 months.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Problem;
